/**
 * Masternodes subpage that is used for `pages/reward-calculator.jsx`.
 *
 * <i>Copyright (c) 2020 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/rewardcalculator.jsx
 * @class RewardCalculator
 * @since 0.7.12
 */

import React, {useState, useEffect} from 'react';
import {Button} from '@rmwc/button';
import {TextField} from '@rmwc/textfield';
import {useStore} from 'effector-react';
import {Snackbar, SnackbarAction} from '@rmwc/snackbar';
import Footer from '../Footer';
import Title from '../Title';
import energi from '../../images/energi.svg';
import translations from '../../lib/translations';
import ProgressCircular from '../storybook/progress-circular';
import rewards from '../../globals/rewards';
import Summary from './rewardcalculatorsummary';

const REGEX_NUMBER = /^\d+$/,
    MAX_AMOUNT_CHARACTERS = 8;

const RewardCalculator = props => {
    let rewardDetails,
        reward = useStore(rewards.$store),
        annualMasternodePercentage = reward.masternode,
        annualStakingPercentage = reward.staking,
        annualCharts;

    const translate = translations.init(props),
        [amount, setAmount] = useState(''),
        [calculation, setCalculation] = useState(''),
        [error, setError] = useState('');

    useEffect(() => {
        rewards.on.startPolling();
        return rewards.on.stopPolling;
    }, []);

    const handleChangeInAmount = e => {
        const value = e.target.value;
        if (((value === '') || value.match(REGEX_NUMBER)) && (value.length <= MAX_AMOUNT_CHARACTERS)) {
            setAmount(value);
        }
    };

    const handleCalculation = () => {
        if (amount.length <= 10) {
            setCalculation(amount);
        }
        else {
            setError('Number too big');
        }
    };

    const handleKeypressInAmount = e => {
        if (e.charCode === 13) {
            handleCalculation();
        }
    };

    if (calculation) {
        rewardDetails = (
            <Summary {...props} amount={parseInt(calculation, 10)} />
        );
    }

    if (typeof annualStakingPercentage !== 'object') {
        annualCharts = (
            <div className="annual-roi">
                <div className="masternode-roi">
                    <p>{translate.t('MASTERNODE_ROI', true)}</p>
                    <div className="chart">
                        <ProgressCircular progress={annualMasternodePercentage || 0} stroke={12} text={`${Math.round(annualMasternodePercentage)}%`} />
                    </div>
                </div>
                <div className="staking-roi">
                    <p>{translate.t('STAKING_ROI', true)}</p>
                    <div className="chart">
                        <ProgressCircular progress={annualStakingPercentage || 0} stroke={12} text={`${Math.round(annualStakingPercentage)}%`} />
                    </div>
                </div>
            </div>
        );
    }

    // TODO: create translation for other languages in /languages/md and /languages

    return (
        <div className="reward-calculator pure-u subpage">
            <Title
                subTitle={translate.md('reward-calculator')}
                title={translate.t('REWARD_CALCULATOR')} />
            <section className="calculator-wrapper">
                <div className="input-wrapper">
                    <TextField
                        className="input"
                        icon={energi}
                        label={translate.t('ENTER_ENERGI_AMOUNT', true)}
                        onChange={handleChangeInAmount}
                        onKeyPress={handleKeypressInAmount}
                        outlined
                        value={amount}/>
                </div>
                <div className="calculate-actions">
                    <Button
                        className="button"
                        label={translate.t('CALCULATE_REWARDS', true)}
                        onClick={handleCalculation}
                        ripple={false}
                        unelevated />
                </div>
                {rewardDetails}
                {annualCharts}
            </section>
            <Snackbar
                action = {
                    <SnackbarAction
                        label={translate.t('CLOSE')}
                        onClick={() => setError('')} />
                }
                message={error}
                onClose={() => setError('')}
                open={!!error} />
            <Footer {...props} />
        </div>
    );
};

export default RewardCalculator;
